import { defineStore } from 'pinia';
import { toast as sonner } from 'vue-sonner';

/**
 * Уведомления.
 *
 * Рендерит `sonner` (<Toaster> в App.vue), стор остался тонкой обёрткой:
 * его вызывают из грида, реалтайма и документов, менять сигнатуры нельзя.
 *
 * Отдельно выделен вид с действием: когда чужая правка перезаписала нашу,
 * пользователю мало сообщения — ему нужна кнопка «вернуть моё значение».
 */

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  message?: string;
  /** Кнопка действия прямо в уведомлении */
  action?: { label: string; run: () => void | Promise<void> };
  /** мс; 0 — висит, пока не закроют руками */
  timeout: number;
  /**
   * Ключ схлопывания. Повторное уведомление с тем же ключом не добавляет
   * новую карточку, а обновляет существующую и увеличивает счётчик.
   */
  dedupeKey?: string;
  count: number;
}

/**
 * Больше четырёх карточек одновременно — это уже не уведомления, а стена,
 * за которой не видно интерфейса. Проверено на пачке чужих массовых правок:
 * без ограничения экран заливает полностью.
 *
 * Отдаётся в <Toaster :visible-toasts>: sonner держит остальные в очереди
 * и показывает их по мере освобождения места.
 */
export const MAX_VISIBLE_TOASTS = 4;

type SonnerId = string | number;

export const useToastStore = defineStore('toast', () => {
  /** Живые карточки со схлопыванием: ключ → id у sonner и счётчик повторов. */
  const groups = new Map<string, { id: SonnerId; count: number }>();

  function push(
    t: Omit<Toast, 'id' | 'timeout' | 'count'> & { timeout?: number; dedupeKey?: string },
  ): string {
    const timeout = t.timeout ?? (t.kind === 'error' ? 8000 : 4000);

    const group = t.dedupeKey ? groups.get(t.dedupeKey) : undefined;
    const count = group ? group.count + 1 : 1;

    // Своего слота под счётчик у sonner нет, поэтому повтор виден в заголовке
    const title = count > 1 ? `${t.title} ×${count}` : t.title;

    const { dedupeKey } = t;
    /** Карточка закрылась — ключ освобождается, следующая серия считается заново. */
    const forget = dedupeKey
      ? () => {
          if (groups.get(dedupeKey)?.id === id) groups.delete(dedupeKey);
        }
      : undefined;

    const id: SonnerId = sonner[t.kind](title, {
      // Тот же id — sonner обновляет карточку на месте, а не добавляет новую
      ...(group ? { id: group.id } : {}),
      ...(t.message ? { description: t.message } : {}),
      // У sonner 0 — это «взять длительность по умолчанию», а не «висеть»
      duration: timeout > 0 ? timeout : Infinity,
      ...(t.action
        ? { action: { label: t.action.label, onClick: () => void t.action?.run() } }
        : {}),
      ...(forget ? { onDismiss: forget, onAutoClose: forget } : {}),
    });

    if (dedupeKey) groups.set(dedupeKey, { id, count });

    return String(id);
  }

  function dismiss(id: string): void {
    sonner.dismiss(id);
  }

  const info = (title: string, message?: string) => push({ kind: 'info', title, message });
  const success = (title: string, message?: string) => push({ kind: 'success', title, message });
  const warning = (title: string, message?: string) => push({ kind: 'warning', title, message });
  const error = (title: string, message?: string) => push({ kind: 'error', title, message });

  return { push, dismiss, info, success, warning, error };
});
